import { UI_ACCENT_COLOUR } from '../styles/uiTokens';
import type { Feature, FeatureCollection } from 'geojson';
import type { AddLayerObject, SourceSpecification } from 'maplibre-gl';

import { isVectorSource, type FeatureProperties, type SupportedGeometry } from '../data/model';
import { evaluateAttributeRule } from '../rules/evaluate';
import { effectiveVectorSourceForLayer } from '../joins/effectiveLayer';
import type { SelectedFeatureRef } from '../selection/model';
import type { RenderScene } from './renderingAdapter';

export const MAPLIBRE_SELECTION_SOURCE_ID = 'dropmap-selection-source';
export const MAPLIBRE_SELECTION_LAYER_IDS = [
  'dropmap-selection-polygon-fill',
  'dropmap-selection-polygon-outline',
  'dropmap-selection-line',
  'dropmap-selection-point',
] as const;

export interface MapLibreSelectionHighlightPlan {
  readonly data: FeatureCollection<SupportedGeometry, FeatureProperties>;
  readonly source: SourceSpecification;
  readonly layers: readonly AddLayerObject[];
}

export function createMapLibreSelectionHighlightPlan(
  scene: RenderScene,
  selection: readonly SelectedFeatureRef[],
): MapLibreSelectionHighlightPlan | null {
  const layersById = new Map(scene.layers.map((layer) => [layer.id, layer]));
  const sourcesById = new Map(
    scene.sources.filter(isVectorSource).map((source) => [source.id, source]),
  );
  const effectiveSourcesByLayer = new Map(
    scene.layers.map((layer) => [layer.id, effectiveVectorSourceForLayer(scene.sources, layer)]),
  );
  const features: Feature<SupportedGeometry, FeatureProperties>[] = [];

  for (const selected of selection) {
    const layer = layersById.get(selected.layerId);
    const source = sourcesById.get(selected.sourceId);
    const effectiveSource = effectiveSourcesByLayer.get(selected.layerId);
    if (!layer || !source || layer.sourceId !== source.id || !layer.visible) continue;
    const feature = source.data.features[selected.sourceFeatureIndex];
    const effectiveFeature = effectiveSource?.data.features[selected.sourceFeatureIndex];
    if (
      !feature?.geometry ||
      !effectiveFeature ||
      !evaluateAttributeRule(layer.displayFilter, effectiveFeature.properties)
    ) {
      continue;
    }
    features.push(feature as Feature<SupportedGeometry, FeatureProperties>);
  }

  if (features.length === 0) return null;
  const data = { type: 'FeatureCollection' as const, features };
  return {
    data,
    source: { type: 'geojson', data },
    layers: [
      {
        id: MAPLIBRE_SELECTION_LAYER_IDS[0],
        type: 'fill',
        source: MAPLIBRE_SELECTION_SOURCE_ID,
        filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': UI_ACCENT_COLOUR, 'fill-opacity': 0.1 },
      },
      {
        id: MAPLIBRE_SELECTION_LAYER_IDS[1],
        type: 'line',
        source: MAPLIBRE_SELECTION_SOURCE_ID,
        filter: ['==', '$type', 'Polygon'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': UI_ACCENT_COLOUR, 'line-opacity': 0.9, 'line-width': 2.5 },
      },
      {
        id: MAPLIBRE_SELECTION_LAYER_IDS[2],
        type: 'line',
        source: MAPLIBRE_SELECTION_SOURCE_ID,
        filter: ['==', '$type', 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': UI_ACCENT_COLOUR, 'line-opacity': 0.82, 'line-width': 5 },
      },
      {
        id: MAPLIBRE_SELECTION_LAYER_IDS[3],
        type: 'circle',
        source: MAPLIBRE_SELECTION_SOURCE_ID,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': 10,
          'circle-color': UI_ACCENT_COLOUR,
          'circle-opacity': 0.12,
          'circle-stroke-color': UI_ACCENT_COLOUR,
          'circle-stroke-opacity': 0.95,
          'circle-stroke-width': 2.5,
        },
      },
    ],
  };
}
